import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Download,
  FileArchive,
  Frame,
  GitBranch,
  ListTree,
  Palette,
  Save,
  ShieldCheck,
  WandSparkles,
  X,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  summarizeOpenPencilFigmaSource,
  type OpenPencilFigmaSourceBundle,
  type OpenPencilSectionFact,
} from "./openpencil-figma-source";
import { OpenPencilBrowserCanvasLazy } from "./openpencil-browser-canvas-lazy";
import {
  summarizeOpenPencilSourceWorkspace,
  type OpenPencilSourceWorkspace,
  type SourceDecisionStatus,
  type SourceSurfaceRole,
} from "./openpencil-source-workspace";
import { buildOpenPencilSourceRoundtripProof } from "./openpencil-source-roundtrip";
import type { OpenPencilSurfaceRevisionDraft } from "./openpencil-source-revision";
import type { OpenPencilBrowserRevisionProof } from "./openpencil-browser-revision-runtime";
import {
  deriveExternalDesignSourceState,
  type ExternalDesignSyncBackEvidence,
  type ExternalFigmaUrlReference,
} from "./external-design-source-state";
import { OpenPencilImportFailureNotice } from "./openpencil-import-failure-notice";
import {
  classifyOpenPencilImportFailureText,
  type OpenPencilImportFailureReason,
} from "./openpencil-import-failure";
import { useStudioStore } from "@/store/studio-store";
import { useStudioLocale } from "@/i18n/use-studio-locale";
import {
  OPENPENCIL_PRIMARY_SOURCE_COPY,
  OPENPENCIL_SOURCE_PANEL_COPY,
} from "./openpencil-primary-source-copy";
import {
  readSourceSummaryCollapsed,
  writeSourceSummaryCollapsed,
  type SourcePreferenceWriteState,
} from "./openpencil-source-panel-preferences";

interface Props {
  bundle: OpenPencilFigmaSourceBundle | null;
  workspace?: OpenPencilSourceWorkspace | null;
  compact?: boolean;
  showDeveloperDetails?: boolean;
  onExportSource?: () => Promise<void> | void;
  onApplySuggestedMapping?: () => void;
  onPageDecision?: (
    pageId: string,
    status: SourceDecisionStatus,
    storyTitle?: string,
  ) => void;
  onSectionDecision?: (
    sectionId: string,
    status: SourceDecisionStatus,
    storyTitle?: string,
  ) => void;
  onSurfaceDecision?: (
    surfaceId: string,
    status: SourceDecisionStatus,
    role: SourceSurfaceRole,
  ) => void;
  onTokenDecision?: (
    tokenId: string,
    status: SourceDecisionStatus,
    canonicalToken?: string,
  ) => void;
  onBulkTokenDecision?: (
    status: Exclude<SourceDecisionStatus, "pending">,
  ) => void;
  /**
   * Registers the accepted token candidates as this project's design system
   * (POST /api/design-system/project-registration). Offered only when a server
   * project is open; accepting tokens without this step leaves the project with
   * no design system at all, which is what the counter below used to hide.
   */
  onRegisterProjectDesignSystem?: () => void;
  registerProjectDesignSystemPending?: boolean;
  /** Outcome line for the last registration attempt — success or refusal. */
  projectDesignSystemNotice?: string | null;
  /** The durable identifier once registered, shown so it is verifiable. */
  registeredProjectDesignSystemId?: string | null;
  onRecordSourceRightsEvidence?: (
    input: SourceRightsEvidenceSelection,
  ) => Promise<void>;
  onReviseSurface?: (draft: OpenPencilSurfaceRevisionDraft) => Promise<void>;
  revisionInFlight?: boolean;
  revisionStatus?: string | null;
  browserRevisionProof?: OpenPencilBrowserRevisionProof | null;
  browserRevisionAdmission?: "pending" | "admitted" | "rejected";
  figmaReference?: ExternalFigmaUrlReference | null;
  syncBackEvidence?: ExternalDesignSyncBackEvidence | null;
  /**
   * Typed reason from the live import attempt. When absent (e.g. a workspace
   * restored from storage) the blocked panel classifies the retained reason
   * text instead — both paths land in the same closed reason set.
   */
  importFailureReason?: OpenPencilImportFailureReason | null;
  /** Offered only when the typed reason says a retry can plausibly work. */
  onRetryImport?: () => void;
  /**
   * Lifts the canvas' frame selection so the Design tab's other screens
   * (Components) highlight the same frame. Omitted → the panels keep the
   * selection to themselves, which is what every embedded use still wants.
   */
  selectedSurfaceId?: string;
  onSelectedSurfaceIdChange?: (surfaceId: string) => void;
}

export type SourceRightsEvidenceSelection =
  | { kind: "user-owned" }
  | { kind: "licensed"; licenseRef: string }
  | { kind: "evaluation-only" };

function MetricTile({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="border-l border-border px-3 first:border-l-0">
      <div className="text-[11px] font-medium text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

function Panel({
  title,
  icon: Icon,
  testId,
  action,
  children,
  collapse,
}: {
  title: string;
  icon: typeof FileArchive;
  testId: string;
  action?: ReactNode;
  children: ReactNode;
  /**
   * Turns the header into a disclosure. `summary` is what stays readable when
   * the body is hidden — nothing is deleted, only folded away.
   */
  collapse?: {
    collapsed: boolean;
    onToggle: () => void;
    summary: ReactNode;
    controlsId: string;
    /** Tooltip only — the button's own text is its accessible name. */
    toggleHint: string;
    testId: string;
    /** Keeps the original source-summary hook unique when panels are nested. */
    summaryTestId?: string;
  };
}) {
  return (
    <section
      className="rounded-lg border border-border bg-card"
      data-testid={testId}
      data-collapsed={collapse ? String(collapse.collapsed) : undefined}
    >
      <header
        className={`flex min-h-12 flex-wrap items-center justify-between gap-x-3 gap-y-1 px-4 py-2 ${
          collapse?.collapsed ? "" : "border-b border-border"
        }`}
      >
        {collapse ? (
          // When collapsed, `basis-full` below `sm` drops the header action
          // onto its own row so the summary is not squeezed down to
          // "[공통] 영업점검…". Expanded there is no summary to protect, so the
          // header stays one row and costs a narrow screen nothing.
          <h2
            className={`flex min-w-0 sm:flex-1 sm:basis-0 ${
              collapse.collapsed ? "basis-full" : "flex-1"
            }`}
          >
            <button
              type="button"
              onClick={collapse.onToggle}
              aria-expanded={!collapse.collapsed}
              aria-controls={collapse.controlsId}
              title={collapse.toggleHint}
              data-testid={collapse.testId}
              className="-mx-1 flex min-w-0 flex-1 items-center gap-2 rounded-md px-1 py-1 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                  collapse.collapsed ? "-rotate-90" : ""
                }`}
                aria-hidden
              />
              <Icon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              {/* Narrow screens stack the summary under the title instead of
                  truncating it away — a collapsed panel whose one-line summary
                  is invisible would be information removed, not folded. */}
              <span className="flex min-w-0 flex-1 flex-col sm:flex-row sm:items-baseline sm:gap-2">
                <span className="whitespace-nowrap text-sm font-semibold text-foreground">
                  {title}
                </span>
                {collapse.collapsed ? (
                  <span
                    className="min-w-0 truncate text-xs font-normal text-muted-foreground"
                    data-testid={
                      collapse.summaryTestId ?? "openpencil-source-summary-line"
                    }
                  >
                    {collapse.summary}
                  </span>
                ) : null}
              </span>
            </button>
          </h2>
        ) : (
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <h2 className="truncate text-sm font-semibold text-foreground">
              {title}
            </h2>
          </div>
        )}
        {action}
      </header>
      <div
        id={collapse?.controlsId}
        className={collapse?.collapsed ? "hidden" : "p-4"}
        hidden={collapse?.collapsed ?? false}
      >
        {children}
      </div>
    </section>
  );
}

function DecisionControl({
  label,
  status,
  onChange,
}: {
  label: string;
  status: SourceDecisionStatus;
  onChange?: (status: SourceDecisionStatus) => void;
}) {
  const { locale } = useStudioLocale();
  const sourceCopy = OPENPENCIL_SOURCE_PANEL_COPY[locale];
  const copy = sourceCopy.decisions;
  if (!onChange) {
    return (
      <span className="text-xs text-muted-foreground">
        {sourceCopy.statusLabel(status)}
      </span>
    );
  }
  const items = [
    { status: "accepted" as const, icon: Check, title: `${copy.accept} ${label}` },
    { status: "pending" as const, icon: Clock3, title: `${copy.defer} ${label}` },
    { status: "rejected" as const, icon: X, title: `${copy.reject} ${label}` },
  ];
  return (
    <div className="inline-flex h-8 shrink-0 overflow-hidden rounded-md border border-border bg-background">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.status}
            type="button"
            title={item.title}
            aria-label={item.title}
            aria-pressed={status === item.status}
            onClick={() => onChange(item.status)}
            className={`flex h-8 w-8 items-center justify-center border-l border-border first:border-l-0 ${
              status === item.status
                ? "bg-foreground text-background"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}

function StoryInput({
  label,
  value,
  onCommit,
}: {
  label: string;
  value?: string;
  onCommit?: (value: string) => void;
}) {
  const { locale } = useStudioLocale();
  const copy = OPENPENCIL_SOURCE_PANEL_COPY[locale].decisions;
  if (!onCommit && !value) return null;
  return (
    <input
      key={value}
      aria-label={`${label} story title`}
      defaultValue={value}
      readOnly={!onCommit}
      placeholder={copy.storyTitle}
      onBlur={(event) => {
        const next = event.currentTarget.value.trim();
        if (onCommit && next !== (value ?? "")) onCommit(next);
      }}
      className="h-8 min-w-0 flex-1 border-b border-border bg-transparent px-1 text-xs text-foreground outline-none focus:border-foreground read-only:text-muted-foreground"
    />
  );
}

const SOURCE_SUMMARY_BODY_ID = "openpencil-source-summary-body";

/**
 * Collapse state for the "Design source" panel, remembered per project.
 *
 * Default when the project has never recorded a choice: COLLAPSED. The
 * imported design is the primary result, so its editable canvas owns the
 * first screen. File identity and frame count remain visible in the summary,
 * and the complete readback/evidence stays one disclosure click away. From
 * the moment the creator makes a choice it is theirs and reopening the
 * project honours it.
 */
function useSourceSummaryCollapsed(): {
  collapsed: boolean;
  toggle: () => void;
  persistence: SourcePreferenceWriteState | null;
} {
  const projectId = useStudioStore((state) => state.currentProjectId);
  const [collapsed, setCollapsed] = useState(
    () => readSourceSummaryCollapsed(projectId) ?? true,
  );
  const [persistence, setPersistence] =
    useState<SourcePreferenceWriteState | null>(null);
  // Switching projects re-reads that project's own choice (and drops any
  // "could not remember" notice, which belonged to the previous one).
  useEffect(() => {
    setCollapsed(readSourceSummaryCollapsed(projectId) ?? true);
    setPersistence(null);
  }, [projectId]);
  const toggle = useCallback(() => {
    setCollapsed((current) => {
      const next = !current;
      setPersistence(writeSourceSummaryCollapsed(next, projectId));
      return next;
    });
  }, [projectId]);
  return { collapsed, toggle, persistence };
}

export function OpenPencilFigmaSourcePanels({
  bundle,
  workspace,
  compact,
  showDeveloperDetails,
  onExportSource,
  onApplySuggestedMapping,
  onPageDecision,
  onSectionDecision,
  onSurfaceDecision,
  onTokenDecision,
  onBulkTokenDecision,
  onRegisterProjectDesignSystem,
  registerProjectDesignSystemPending,
  projectDesignSystemNotice,
  registeredProjectDesignSystemId,
  onRecordSourceRightsEvidence,
  onReviseSurface,
  revisionInFlight,
  revisionStatus,
  browserRevisionProof,
  browserRevisionAdmission,
  figmaReference,
  syncBackEvidence,
  importFailureReason,
  onRetryImport,
  selectedSurfaceId: controlledSurfaceId,
  onSelectedSurfaceIdChange,
}: Props) {
  const { locale } = useStudioLocale();
  const primaryCopy = OPENPENCIL_PRIMARY_SOURCE_COPY[locale].panels;
  const sourceCopy = OPENPENCIL_SOURCE_PANEL_COPY[locale];
  const pages = useMemo(() => bundle?.sourceFacts?.pages ?? [], [bundle]);
  const {
    collapsed: summaryCollapsed,
    toggle: toggleSummaryCollapsed,
    persistence: summaryPersistence,
  } = useSourceSummaryCollapsed();
  const [selectedPageId, setSelectedPageId] = useState(pages[0]?.id ?? "");
  const emptyPageSelectionRef = useRef<{
    pageId: string;
    priorSurfaceId: string;
  } | null>(null);
  const [ownSurfaceId, setOwnSurfaceId] = useState("");
  const selectedSurfaceId = controlledSurfaceId ?? ownSurfaceId;
  const setSelectedSurfaceId = useCallback(
    (surfaceId: string) => {
      setOwnSurfaceId(surfaceId);
      onSelectedSurfaceIdChange?.(surfaceId);
    },
    [onSelectedSurfaceIdChange],
  );
  const [exportingSource, setExportingSource] = useState(false);
  const [revisingSurface, setRevisingSurface] = useState(false);
  const [revisingComponent, setRevisingComponent] = useState(false);
  const [editName, setEditName] = useState("");
  const [editWidth, setEditWidth] = useState(0);
  const [editHeight, setEditHeight] = useState(0);
  const [editVisibility, setEditVisibility] = useState<
    "unchanged" | "visible" | "hidden"
  >("unchanged");
  const [editOpacity, setEditOpacity] = useState(1);
  const [opacityTouched, setOpacityTouched] = useState(false);
  const componentSets = useMemo(
    () => bundle?.sourceFacts?.uiOntology?.componentSets ?? [],
    [bundle],
  );
  const [selectedComponentSetId, setSelectedComponentSetId] = useState("");
  const [selectedComponentVariantId, setSelectedComponentVariantId] =
    useState("");
  const [componentOpacity, setComponentOpacity] = useState(1);
  const [componentOpacityTouched, setComponentOpacityTouched] = useState(false);
  const [componentFillColor, setComponentFillColor] = useState("#000000");
  const [componentFillTouched, setComponentFillTouched] = useState(false);
  const [sourceRightsChoice, setSourceRightsChoice] = useState<
    SourceRightsEvidenceSelection["kind"] | ""
  >("");
  const [sourceRightsLicenseRef, setSourceRightsLicenseRef] = useState("");
  const [recordingSourceRights, setRecordingSourceRights] = useState(false);
  const [sourceRightsError, setSourceRightsError] = useState<string | null>(
    null,
  );
  const sourceRightsRecorded = Boolean(workspace?.sourceRightsDeclaration);
  const [sourceRightsCollapsed, setSourceRightsCollapsed] = useState(
    sourceRightsRecorded,
  );
  const [readinessCollapsed, setReadinessCollapsed] = useState(true);
  const [advancedReviewCollapsed, setAdvancedReviewCollapsed] = useState(true);
  useEffect(() => {
    setSourceRightsCollapsed(sourceRightsRecorded);
  }, [sourceRightsRecorded, workspace?.sourceId]);
  useEffect(() => {
    if (!componentSets.some((item) => item.id === selectedComponentSetId)) {
      setSelectedComponentSetId(componentSets[0]?.id ?? "");
    }
  }, [componentSets, selectedComponentSetId]);
  const selectedComponentSet = componentSets.find(
    (item) => item.id === selectedComponentSetId,
  );
  const componentVariants = selectedComponentSet?.variantSamples ?? [];
  useEffect(() => {
    if (
      !componentVariants.some((item) => item.id === selectedComponentVariantId)
    ) {
      setSelectedComponentVariantId(componentVariants[0]?.id ?? "");
    }
  }, [componentVariants, selectedComponentVariantId]);
  const selectedComponentVariant = componentVariants.find(
    (item) => item.id === selectedComponentVariantId,
  );
  const componentEditableSnapshot = selectedComponentVariant?.editableSnapshot;
  const observedComponentOpacity = componentEditableSnapshot?.opacity ?? 1;
  useEffect(() => {
    setComponentOpacity(observedComponentOpacity);
    setComponentOpacityTouched(false);
    setComponentFillColor("#000000");
    setComponentFillTouched(false);
  }, [observedComponentOpacity, selectedComponentVariant]);
  const componentRevisionChanged = Boolean(
    (componentEditableSnapshot &&
      componentOpacityTouched &&
      Math.abs(componentOpacity - observedComponentOpacity) > 0.000_001) ||
    componentFillTouched,
  );
  const componentRevisionLocked = revisingComponent || revisionInFlight;
  useEffect(() => {
    if (!pages.some((page) => page.id === selectedPageId)) {
      setSelectedPageId(pages[0]?.id ?? "");
    }
  }, [pages, selectedPageId]);

  const sectionsByPage = useMemo(() => {
    const grouped = new Map<string, OpenPencilSectionFact[]>();
    for (const section of bundle?.sourceFacts?.storySections ?? []) {
      const current = grouped.get(section.pageId) ?? [];
      current.push(section);
      grouped.set(section.pageId, current);
    }
    return grouped;
  }, [bundle]);
  const allSurfaces = useMemo(
    () => bundle?.sourceFacts?.surfaceCandidates ?? [],
    [bundle],
  );
  const selectedSurfaces = useMemo(
    () =>
      selectedPageId
        ? allSurfaces.filter((surface) => surface.pageId === selectedPageId)
        : allSurfaces,
    [allSurfaces, selectedPageId],
  );
  /**
   * Canvas → panel selection. The canvas spans every source page, so picking a
   * frame from another page also moves the page-scoped mapper/list/editor onto
   * that page; without it the surface would be selected but invisible below.
   */
  const selectSurfaceFromCanvas = useCallback(
    (surfaceId: string) => {
      const surface = allSurfaces.find((item) => item.id === surfaceId);
      if (!surface) return;
      emptyPageSelectionRef.current = null;
      setSelectedPageId(surface.pageId);
      setSelectedSurfaceId(surfaceId);
    },
    [allSurfaces, setSelectedSurfaceId],
  );
  /**
   * Picking a page directly moves the frame selection with it. Doing it here
   * rather than in the reconcile effect below is what lets that effect treat
   * an out-of-page selection as "follow it" (a frame chosen on the Components
   * screen) instead of "snap back".
   */
  const selectPage = useCallback(
    (pageId: string) => {
      const firstSurface = allSurfaces.find(
        (surface) => surface.pageId === pageId,
      );
      emptyPageSelectionRef.current = firstSurface
        ? null
        : { pageId, priorSurfaceId: selectedSurfaceId };
      setSelectedPageId(pageId);
      if (firstSurface) setSelectedSurfaceId(firstSurface.id);
    },
    [allSurfaces, selectedSurfaceId, setSelectedSurfaceId],
  );
  useEffect(() => {
    const emptyPageSelection = emptyPageSelectionRef.current;
    if (
      emptyPageSelection?.pageId === selectedPageId &&
      emptyPageSelection.priorSurfaceId === selectedSurfaceId
    ) {
      return;
    }
    emptyPageSelectionRef.current = null;
    const surface = allSurfaces.find((item) => item.id === selectedSurfaceId);
    if (!surface) {
      const fallback = selectedSurfaces[0]?.id ?? "";
      if (fallback !== selectedSurfaceId) setSelectedSurfaceId(fallback);
      return;
    }
    if (surface.pageId !== selectedPageId) setSelectedPageId(surface.pageId);
  }, [
    allSurfaces,
    selectedPageId,
    selectedSurfaceId,
    selectedSurfaces,
    setSelectedSurfaceId,
  ]);
  const selectedSurface = selectedSurfaces.find(
    (surface) => surface.id === selectedSurfaceId,
  );
  useEffect(() => {
    setEditName(selectedSurface?.name ?? "");
    setEditWidth(selectedSurface?.width ?? 0);
    setEditHeight(selectedSurface?.height ?? 0);
    setEditVisibility("unchanged");
    setEditOpacity(1);
    setOpacityTouched(false);
  }, [selectedSurface]);
  const surfaceRevisionChanged = Boolean(
    selectedSurface &&
    (editName.trim() !== selectedSurface.name ||
      editWidth !== selectedSurface.width ||
      editHeight !== selectedSurface.height ||
      editVisibility !== "unchanged" ||
      opacityTouched),
  );

  useEffect(() => {
    const declaration = workspace?.sourceRightsDeclaration;
    if (!declaration) {
      setSourceRightsChoice("");
      setSourceRightsLicenseRef("");
      setSourceRightsError(null);
      return;
    }
    const choice =
      declaration.declaration.requestedUse === "evaluation-only"
        ? "evaluation-only"
        : declaration.declaration.rightsBasis === "user-owned" ||
            declaration.declaration.rightsBasis === "licensed"
          ? declaration.declaration.rightsBasis
          : "";
    setSourceRightsChoice(choice);
    setSourceRightsLicenseRef(declaration.licenseRef ?? "");
    setSourceRightsError(null);
  }, [workspace?.sourceId, workspace?.sourceRightsDeclaration]);

  if (!bundle) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/25 p-4 text-sm text-muted-foreground">
        {sourceCopy.noDesignImported}
      </div>
    );
  }

  const summary = summarizeOpenPencilFigmaSource(bundle);
  const progress = summarizeOpenPencilSourceWorkspace(workspace ?? null);
  const roundtrip = buildOpenPencilSourceRoundtripProof(
    workspace ?? null,
    bundle,
  );
  const persistedUpstream = workspace?.figmaCompanion.upstreamFigma;
  const externalDesignState = deriveExternalDesignSourceState({
    figmaReference:
      figmaReference ??
      (persistedUpstream
        ? {
            fileKey: persistedUpstream.fileKey,
            nodeId: persistedUpstream.nodeId,
            url: persistedUpstream.url,
            observedAt: persistedUpstream.observedAt,
            version: persistedUpstream.upstreamVersion,
            hash: persistedUpstream.upstreamHash,
          }
        : null),
    workspace,
    syncBack:
      syncBackEvidence ?? workspace?.figmaCompanion.syncBackEvidence ?? null,
  });
  const tokens = bundle.sourceFacts?.designTokens ?? [];
  const colorEvidence = bundle.sourceFacts?.visualTokenEvidence ?? [];
  const activeAdmission =
    workspace?.bundle.generationAdmission ?? bundle.generationAdmission;
  const requiredGates = new Set(activeAdmission.requiredGateIds);
  const requiredReleaseGates = new Set(progress.requiredReleaseGateIds);
  const readiness = [
    {
      label: sourceCopy.readiness.sourceMapping,
      ready: !requiredGates.has("source-mapping-approved"),
      detail: sourceCopy.readiness.surfacesSelected(
        progress.surfaces.accepted,
        progress.surfaces.total,
      ),
    },
    {
      label: sourceCopy.readiness.designTokens,
      ready: !requiredGates.has("design-token-decisions-approved"),
      detail: sourceCopy.readiness.decisionsPending(progress.tokens.pending),
    },
    {
      label: sourceCopy.readiness.visualEvidence,
      ready:
        !requiredGates.has("surface-preview-evidence") &&
        !requiredGates.has("visual-semantics-approved"),
      detail: sourceCopy.readiness.previews(
        bundle.visualPreviewManifest?.rendered ?? 0,
        bundle.visualPreviewManifest?.requested ?? summary.frameCandidates,
        progress.visualJudgements,
      ),
    },
    {
      label: sourceCopy.readiness.revisionDiff,
      ready:
        !bundle.revisionLineage ||
        progress.revisionVisualDiffStatus === "approved",
      detail: bundle.revisionLineage
        ? sourceCopy.readiness.changed(
            progress.revisionChangedSurfaceCount,
            progress.revisionVisualDiffDisposition,
          )
        : sourceCopy.readiness.notRequired,
    },
    {
      label: sourceCopy.readiness.assembly,
      ready: !requiredGates.has("application-assembly-validated"),
      detail: sourceCopy.statusLabel(progress.assemblyStatus),
    },
    {
      label: sourceCopy.readiness.candidate,
      ready: !requiredReleaseGates.has("source-candidate-bound"),
      detail:
        progress.candidateId ?? sourceCopy.statusLabel(progress.candidateStatus),
    },
    {
      label: sourceCopy.readiness.runtimeProof,
      ready: !requiredReleaseGates.has("runtime-proof-passed"),
      detail: `${sourceCopy.statusLabel(progress.runtimeProofStatus)} · ${sourceCopy.statusLabel(progress.releaseAdmission)}`,
    },
  ];
  const readyGateCount = readiness.filter((item) => item.ready).length;
  const nextReadinessAction = readiness.find((item) => !item.ready)?.label;
  const readinessSummary = `${sourceCopy.readiness.readyCount(
    readyGateCount,
    readiness.length,
  )} · ${
    nextReadinessAction
      ? sourceCopy.readiness.next(nextReadinessAction)
      : sourceCopy.readiness.complete
  }`;

  // A blocked generation admission does not mean the retained .fig failed to
  // parse. Keep genuine import failures in this recovery state, but let a
  // readable source open in the browser editor while its review gates remain.
  const hasReadableSource = bundle.readback !== null && Boolean(bundle.sourceArtifactRef);
  if (!hasReadableSource) {
    // UX audit (2026-07-27) finding 3: this panel used to print the raw
    // upstream body ("Not found") as the creator's whole explanation. The
    // retained reason stays as evidence, but the creator now reads typed
    // copy — what failed, what it means, one next action.
    return (
      <div className="space-y-3" data-testid="openpencil-blocked-evidence">
        <OpenPencilImportFailureNotice
          reason={
            importFailureReason ??
            classifyOpenPencilImportFailureText(bundle.blockedEvidence?.reason)
          }
          fileName={bundle.fileName}
          detail={bundle.blockedEvidence?.reason ?? null}
          showDeveloperDetails={showDeveloperDetails}
          {...(onRetryImport ? { onRetry: onRetryImport } : {})}
          testId="openpencil-blocked-notice"
        />
        <div
          className="border-l-2 border-foreground pl-3 text-xs text-muted-foreground"
          data-testid="openpencil-blocked-admission"
        >
          {sourceCopy.generationAdmissionBlocked}
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <Panel
        title={primaryCopy.designSource}
        icon={FileArchive}
        testId="openpencil-canary-readback"
        collapse={{
          collapsed: summaryCollapsed,
          onToggle: toggleSummaryCollapsed,
          controlsId: SOURCE_SUMMARY_BODY_ID,
          testId: "openpencil-source-summary-toggle",
          toggleHint: summaryCollapsed
            ? primaryCopy.showDetails
            : primaryCopy.hideDetails,
          summary: `${bundle.fileName} · ${primaryCopy.frameCount(summary.frameCandidates)}`,
        }}
        action={
          onExportSource ? (
            <button
              type="button"
              disabled={exportingSource}
              onClick={() => {
                setExportingSource(true);
                void Promise.resolve(onExportSource()).finally(() =>
                  setExportingSource(false),
                );
              }}
              className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-wait disabled:opacity-60"
            >
              <Download className="h-3.5 w-3.5" />
              {exportingSource ? primaryCopy.exporting : primaryCopy.exportCurrent}
            </button>
          ) : null
        }
      >
        <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-4 lg:grid-cols-8">
          <MetricTile label={sourceCopy.metrics.pages} value={summary.pages} />
          <MetricTile label={sourceCopy.metrics.nodes} value={summary.totalNodes} />
          <MetricTile label={sourceCopy.metrics.sections} value={summary.sections} />
          <MetricTile label={sourceCopy.metrics.candidates} value={summary.frameCandidates} />
          <MetricTile label={sourceCopy.metrics.mobile} value={summary.mobileSizedFrames} />
          <MetricTile label={sourceCopy.metrics.reference} value={summary.nonMobileFrames} />
          <MetricTile label={sourceCopy.metrics.variables} value={summary.variables} />
          <MetricTile label={sourceCopy.metrics.colors} value={summary.detectedColors} />
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="font-medium text-foreground">{bundle.fileName}</span>
          {workspace ? (
            <span className="ml-auto">
              {sourceCopy.sourceRevision(
                progress.sourceRevision,
                workspace.revision,
                progress.surfaces.pending,
              )}
            </span>
          ) : null}
        </div>
        {workspace ? (
          <div
            className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3"
            data-testid="external-design-source-state"
            data-state={externalDesignState.state}
          >
            {externalDesignState.labels.map((label) => (
              <span
                key={label}
                className="rounded-full border border-border bg-background px-2 py-1 text-[10px] font-medium text-foreground"
              >
                {label}
              </span>
            ))}
            <span className="text-[10px] text-muted-foreground">
              {sourceCopy.fromDesignFile(progress.sourceRevision)}
            </span>
          </div>
        ) : null}
        <div
          className="mt-3 flex min-w-0 items-start gap-2 border-t border-border pt-3"
          data-testid="openpencil-source-roundtrip"
          data-roundtrip-status={roundtrip.status}
        >
          {roundtrip.claimAllowed ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
          ) : (
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-2">
              <span className="text-xs font-semibold text-foreground">
                {sourceCopy.roundtrip}
              </span>
              <span className="truncate text-[10px] text-muted-foreground">
                {roundtrip.status}
              </span>
            </div>
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
              {roundtrip.summary}
            </p>
            {roundtrip.currentSha256 ? (
              <p
                className="mt-1 truncate font-mono text-[10px] text-muted-foreground"
                title={roundtrip.currentSha256}
              >
                sha256 {roundtrip.currentSha256}
              </p>
            ) : null}
          </div>
        </div>
      </Panel>

      {summaryPersistence === "rejected" ? (
        // Honest failure: this browser refused to store the choice (private
        // mode / full quota). Saying nothing would let the panel look like it
        // had remembered, and the next visit would silently contradict it.
        <p
          className="text-xs text-muted-foreground"
          role="status"
          data-testid="openpencil-source-summary-persistence-warning"
        >
          {sourceCopy.preferenceNotSaved}
        </p>
      ) : null}

      {!compact ? (
        <Panel
          title={primaryCopy.canvas}
          icon={Frame}
          testId="openpencil-source-canvas-panel"
        >
          <OpenPencilBrowserCanvasLazy
            bundle={bundle}
            selectedSurfaceId={selectedSurface?.id}
            onSelectedSurfaceIdChange={selectSurfaceFromCanvas}
            onActivePageIdChange={selectPage}
          />
        </Panel>
      ) : null}

      {workspace ? (
        <Panel
          title={primaryCopy.sourceUseEvidence}
          icon={ShieldCheck}
          testId="openpencil-source-rights-evidence"
          {...(sourceRightsRecorded
            ? {
                collapse: {
                  collapsed: sourceRightsCollapsed,
                  onToggle: () => setSourceRightsCollapsed((value) => !value),
                  summary:
                    workspace.sourceRightsDeclaration?.review.status ===
                    "evaluation-only"
                      ? sourceCopy.rights.evaluationStatus
                      : sourceCopy.rights.recordedStatus,
                  controlsId: "openpencil-source-rights-body",
                  toggleHint: sourceRightsCollapsed
                    ? sourceCopy.rights.show
                    : sourceCopy.rights.hide,
                  testId: "openpencil-source-rights-toggle",
                  summaryTestId: "openpencil-source-rights-summary",
                },
              }
            : {})}
        >
          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,0.8fr)]">
            <fieldset
              aria-describedby="openpencil-source-rights-help"
              disabled={!onRecordSourceRightsEvidence || recordingSourceRights}
            >
              <legend className="text-xs font-semibold text-foreground">
                {sourceCopy.rights.question}
              </legend>
              <div className="mt-3 grid gap-2">
                {[
                  {
                    value: "user-owned" as const,
                    label: sourceCopy.rights.owned,
                    detail: sourceCopy.rights.ownedDetail,
                  },
                  {
                    value: "licensed" as const,
                    label: sourceCopy.rights.licensed,
                    detail: sourceCopy.rights.licensedDetail,
                  },
                  {
                    value: "evaluation-only" as const,
                    label: sourceCopy.rights.evaluation,
                    detail: sourceCopy.rights.evaluationDetail,
                  },
                ].map((item) => (
                  <label
                    key={item.value}
                    className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background px-3 py-2 text-xs text-foreground has-[:checked]:border-foreground disabled:cursor-not-allowed"
                  >
                    <input
                      type="radio"
                      name="openpencil-source-rights"
                      value={item.value}
                      checked={sourceRightsChoice === item.value}
                      onChange={() => {
                        setSourceRightsChoice(item.value);
                        setSourceRightsError(null);
                      }}
                      className="mt-0.5"
                    />
                    <span>
                      <span className="block font-medium">{item.label}</span>
                      <span className="mt-0.5 block leading-4 text-muted-foreground">
                        {item.detail}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {sourceRightsChoice === "licensed" ? (
                <label className="mt-3 block text-[11px] font-medium text-muted-foreground">
                  {sourceCopy.rights.licenseReference}
                  <input
                    required
                    aria-invalid={Boolean(
                      sourceRightsError && !sourceRightsLicenseRef.trim(),
                    )}
                    aria-describedby="openpencil-source-rights-help"
                    value={sourceRightsLicenseRef}
                    onChange={(event) => {
                      setSourceRightsLicenseRef(event.currentTarget.value);
                      setSourceRightsError(null);
                    }}
                    placeholder={sourceCopy.rights.licensePlaceholder}
                    className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                  />
                </label>
              ) : null}
            </fieldset>
            <div className="rounded-md border border-border bg-muted/20 p-3">
              <p
                id="openpencil-source-rights-help"
                className="text-[11px] leading-5 text-muted-foreground"
              >
                {sourceCopy.rights.help}
              </p>
              {workspace.sourceRightsDeclaration ? (
                <div
                  className="mt-3 border-t border-border pt-3 text-xs font-medium text-foreground"
                  role="status"
                  aria-live="polite"
                >
                  {workspace.sourceRightsDeclaration.review.status ===
                  "evaluation-only"
                    ? sourceCopy.rights.evaluationStatus
                    : sourceCopy.rights.recordedStatus}
                  <div className="mt-1 break-all font-mono text-[10px] font-normal text-muted-foreground">
                    {
                      workspace.sourceRightsDeclaration.evidenceRef
                        .artifactHash
                    }
                  </div>
                </div>
              ) : null}
              {sourceRightsError ? (
                <p
                  className="mt-3 text-[11px] leading-4 text-destructive"
                  role="alert"
                >
                  {sourceRightsError}
                </p>
              ) : null}
              {onRecordSourceRightsEvidence ? (
                <button
                  type="button"
                  disabled={
                    !sourceRightsChoice ||
                    recordingSourceRights ||
                    (sourceRightsChoice === "licensed" &&
                      !sourceRightsLicenseRef.trim())
                  }
                  onClick={() => {
                    const selection: SourceRightsEvidenceSelection =
                      sourceRightsChoice === "licensed"
                        ? {
                            kind: "licensed",
                            licenseRef: sourceRightsLicenseRef.trim(),
                          }
                        : sourceRightsChoice === "user-owned"
                          ? { kind: "user-owned" }
                          : { kind: "evaluation-only" };
                    setRecordingSourceRights(true);
                    setSourceRightsError(null);
                    void onRecordSourceRightsEvidence(selection)
                      .catch((error) => {
                        setSourceRightsError(
                          error instanceof Error
                            ? error.message
                            : sourceCopy.rights.recordFailed,
                        );
                      })
                      .finally(() => setRecordingSourceRights(false));
                  }}
                  className="mt-3 inline-flex h-8 items-center gap-1.5 rounded-md bg-foreground px-2.5 text-xs font-medium text-background hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden />
                  {recordingSourceRights
                    ? sourceCopy.rights.recording
                    : sourceCopy.rights.record}
                </button>
              ) : null}
            </div>
          </div>
        </Panel>
      ) : null}

      <Panel
        title={sourceCopy.readiness.title}
        icon={ShieldCheck}
        testId="openpencil-generation-readiness"
        collapse={{
          collapsed: readinessCollapsed,
          onToggle: () => setReadinessCollapsed((value) => !value),
          summary: readinessSummary,
          controlsId: "openpencil-generation-readiness-body",
          toggleHint: readinessCollapsed
            ? sourceCopy.readiness.show
            : sourceCopy.readiness.hide,
          testId: "openpencil-generation-readiness-toggle",
          summaryTestId: "openpencil-generation-readiness-summary",
        }}
      >
        <div className="grid gap-x-5 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-7">
          {readiness.map((item) => (
            <div
              key={item.label}
              className="flex min-h-14 items-start gap-2 border-b border-border py-2 xl:border-b-0 xl:border-r xl:pr-4 xl:last:border-r-0"
            >
              {item.ready ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-foreground" />
              ) : (
                <Clock3 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              )}
              <div className="min-w-0">
                <div className="text-xs font-semibold text-foreground">
                  {item.label}
                </div>
                <div className="mt-1 text-xs leading-4 text-muted-foreground">
                  {item.detail}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Panel>

      {workspace?.revisionVisualDiff || !compact || showDeveloperDetails ? (
        <Panel
          title={sourceCopy.advanced.title}
          icon={ListTree}
          testId="openpencil-advanced-source-review"
          collapse={{
            collapsed: advancedReviewCollapsed,
            onToggle: () => setAdvancedReviewCollapsed((value) => !value),
            summary: sourceCopy.advanced.summary(
              summary.pages,
              summary.frameCandidates,
              progress.tokens.pending,
            ),
            controlsId: "openpencil-advanced-source-review-body",
            toggleHint: advancedReviewCollapsed
              ? sourceCopy.advanced.show
              : sourceCopy.advanced.hide,
            testId: "openpencil-advanced-source-review-toggle",
            summaryTestId: "openpencil-advanced-source-review-summary",
          }}
        >
          <div className="space-y-4">
      {workspace?.revisionVisualDiff ? (
        <Panel
          title={sourceCopy.revision.title}
          icon={GitBranch}
          testId="openpencil-revision-visual-diff"
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <MetricTile
              label={sourceCopy.revision.changedSurfaces}
              value={workspace.revisionVisualDiff.changedSurfaceIds.length}
            />
            <MetricTile
              label={sourceCopy.revision.disposition}
              value={sourceCopy.statusLabel(
                workspace.revisionVisualDiff.disposition,
              )}
            />
            <MetricTile
              label={sourceCopy.revision.status}
              value={sourceCopy.statusLabel(workspace.revisionVisualDiff.status)}
            />
          </div>
          {workspace.revisionVisualDiff.changedSurfaceIds.length > 0 ? (
            <div className="mt-4 border-t border-border pt-3">
              <p className="text-xs font-medium text-foreground">
                {sourceCopy.revision.reviewRequired}
              </p>
              <p className="mt-1 text-xs leading-5 text-muted-foreground">
                {sourceCopy.revision.reviewDetail(
                  workspace.revisionVisualDiff.changedSurfaceIds.length,
                )}
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {workspace.revisionVisualDiff.changedSurfaceIds
                  .slice(0, 8)
                  .map((surfaceId) => (
                    <span
                      key={surfaceId}
                      className="rounded border border-border bg-muted px-2 py-1 font-mono text-[10px] text-muted-foreground"
                    >
                      {surfaceId}
                    </span>
                  ))}
              </div>
            </div>
          ) : null}
          {workspace.revisionVisualDiff.blockers.length > 0 ? (
            <div className="mt-4 border-l-2 border-destructive pl-3 text-xs leading-5 text-destructive">
              {workspace.revisionVisualDiff.blockers[0]}
            </div>
          ) : null}
        </Panel>
      ) : null}

      {!compact ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <Panel
            title={sourceCopy.sourceTree}
            icon={ListTree}
            testId="openpencil-source-tree"
            action={
              onApplySuggestedMapping ? (
                <button
                  type="button"
                  onClick={onApplySuggestedMapping}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs font-medium text-foreground hover:bg-muted"
                >
                  <WandSparkles className="h-3.5 w-3.5" />
                  {sourceCopy.applySuggestions}
                </button>
              ) : null
            }
          >
            <div className="max-h-[560px] overflow-auto">
              {pages.map((page) => {
                const pageDecision = workspace?.pageDecisions[page.id];
                const sections = sectionsByPage.get(page.id) ?? [];
                return (
                  <div
                    key={page.id}
                    className="border-b border-border py-3 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => selectPage(page.id)}
                        className={`min-w-0 flex-1 text-left ${
                          selectedPageId === page.id
                            ? "text-foreground"
                            : "text-muted-foreground"
                        }`}
                      >
                        <span className="block truncate text-sm font-semibold">
                          {page.name}
                        </span>
                        <span className="mt-0.5 block text-xs">
                          {sourceCopy.pageNodes(page.nodeCount, sections.length)}
                        </span>
                      </button>
                      <DecisionControl
                        label={page.name}
                        status={pageDecision?.status ?? "pending"}
                        onChange={
                          onPageDecision
                            ? (status) =>
                                onPageDecision(
                                  page.id,
                                  status,
                                  pageDecision?.storyTitle,
                                )
                            : undefined
                        }
                      />
                    </div>
                    {pageDecision || sections.length > 0 ? (
                      <div className="ml-3 mt-2 border-l border-border pl-3">
                        {pageDecision ? (
                          <StoryInput
                            label={page.name}
                            value={pageDecision.storyTitle}
                            onCommit={
                              onPageDecision
                                ? (storyTitle) =>
                                    onPageDecision(
                                      page.id,
                                      pageDecision.status,
                                      storyTitle,
                                    )
                                : undefined
                            }
                          />
                        ) : null}
                        {sections.map((section) => {
                          const decision =
                            workspace?.sectionDecisions[section.id];
                          return (
                            <div
                              key={section.id}
                              className="flex min-h-10 items-center gap-2 border-b border-border/60 last:border-b-0"
                            >
                              <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                                {section.name}
                              </span>
                              <StoryInput
                                label={section.name}
                                value={decision?.storyTitle}
                                onCommit={
                                  onSectionDecision
                                    ? (storyTitle) =>
                                        onSectionDecision(
                                          section.id,
                                          decision?.status ?? "pending",
                                          storyTitle,
                                        )
                                    : undefined
                                }
                              />
                              <DecisionControl
                                label={section.name}
                                status={decision?.status ?? "pending"}
                                onChange={
                                  onSectionDecision
                                    ? (status) =>
                                        onSectionDecision(
                                          section.id,
                                          status,
                                          decision?.storyTitle,
                                        )
                                    : undefined
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel
            title={sourceCopy.mapper}
            icon={GitBranch}
            testId="openpencil-page-story-mapper"
          >
            <label className="mb-3 flex items-center gap-3 text-xs text-muted-foreground">
              {sourceCopy.page}
              <select
                value={selectedPageId}
                onChange={(event) => selectPage(event.currentTarget.value)}
                className="h-9 min-w-0 flex-1 rounded-md border border-border bg-background px-2 text-sm text-foreground"
              >
                {pages.map((page) => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </select>
            </label>
            {browserRevisionProof ? (
              <div
                className="mb-3 rounded-md border border-success/30 bg-success/5 px-3 py-2 text-[11px] leading-4 text-success"
                data-testid="openpencil-browser-revision-proof"
              >
                {sourceCopy.browserProof} ·{" "}
                {browserRevisionProof.outputSha256.slice(0, 12)}... ·{" "}
                {browserRevisionAdmission === "admitted"
                  ? sourceCopy.revisionAdmission.admitted
                  : browserRevisionAdmission === "rejected"
                    ? sourceCopy.revisionAdmission.rejected
                    : sourceCopy.revisionAdmission.pending}
              </div>
            ) : null}
            {revisionStatus ? (
              <p
                className="mb-3 text-[11px] leading-4 text-muted-foreground"
                aria-live="polite"
                data-testid="openpencil-source-revision-status"
              >
                {revisionStatus}
              </p>
            ) : null}
            {selectedComponentVariant && onReviseSurface ? (
              <section
                className="mb-3 border-y border-border py-4"
                data-testid="openpencil-component-canary-editor"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xs font-semibold text-foreground">
                      {sourceCopy.component.title}
                    </h3>
                    <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                      {sourceCopy.component.detail}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={
                      !componentRevisionChanged ||
                      componentRevisionLocked ||
                      componentOpacity < 0 ||
                      componentOpacity > 1
                    }
                    onClick={() => {
                      setRevisingComponent(true);
                      void onReviseSurface({
                        surfaceId: selectedComponentVariant.id,
                        nodeId: selectedComponentVariant.id,
                        expectedNodeType: "COMPONENT",
                        expectedName: selectedComponentVariant.name,
                        ...(componentOpacityTouched &&
                        Math.abs(componentOpacity - observedComponentOpacity) >
                          0.000_001
                          ? { opacity: componentOpacity }
                          : {}),
                        ...(componentFillTouched
                          ? { fillColor: componentFillColor }
                          : {}),
                        reason: `Update ${selectedComponentVariant.name} from the Studio component editor.`,
                      })
                        .catch(() => undefined)
                        .finally(() => {
                          setComponentOpacityTouched(false);
                          setComponentFillTouched(false);
                          setRevisingComponent(false);
                        });
                    }}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-foreground px-2.5 text-xs font-medium text-background hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Save className="h-3.5 w-3.5" aria-hidden />
                    {revisingComponent || revisionInFlight
                      ? sourceCopy.component.revising
                      : sourceCopy.component.create}
                  </button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-[11px] font-medium text-muted-foreground">
                    {sourceCopy.component.set}
                    <select
                      value={selectedComponentSetId}
                      disabled={componentRevisionLocked}
                      onChange={(event) =>
                        setSelectedComponentSetId(event.currentTarget.value)
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    >
                      {componentSets.map((componentSet) => (
                        <option key={componentSet.id} value={componentSet.id}>
                          {componentSet.name} ({componentSet.variantCount})
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="text-[11px] font-medium text-muted-foreground">
                    {sourceCopy.component.variant}
                    <select
                      aria-label={sourceCopy.component.variant}
                      value={selectedComponentVariantId}
                      disabled={componentRevisionLocked}
                      onChange={(event) =>
                        setSelectedComponentVariantId(event.currentTarget.value)
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    >
                      {componentVariants.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variant.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div
                    className="rounded-md border border-border bg-muted/20 p-3 text-[11px] leading-4 text-muted-foreground sm:col-span-2"
                    data-testid="openpencil-component-source-contract"
                  >
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-semibold text-foreground">
                        {sourceCopy.component.sourceOnly}
                      </span>
                      <span>
                        {sourceCopy.component.extractor}{" "}
                        {
                          bundle?.sourceFacts?.uiOntology?.extraction
                            .extractorVersion
                        }
                      </span>
                      <span>
                        {sourceCopy.component.sampled}{" "}
                        {selectedComponentSet?.variantSamples.length ?? 0}/
                        {selectedComponentSet?.variantCount ?? 0}
                      </span>
                      <span>
                        {sourceCopy.component.valueCoverage}{" "}
                        {selectedComponentSet?.variantCoverage
                          ? selectedComponentSet.variantCoverage
                              .completeValueCoverage
                            ? sourceCopy.component.complete
                            : sourceCopy.component.partial
                          : sourceCopy.component.legacyUnknown}
                      </span>
                      <span>
                        {sourceCopy.component.editableSnapshot}{" "}
                        {componentEditableSnapshot
                          ? sourceCopy.component.available
                          : sourceCopy.component.unavailable}
                      </span>
                    </div>
                    <p className="mt-1">
                      {sourceCopy.component.semanticBoundary}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {selectedComponentSet?.propertyDefinitions.map(
                        (property) => (
                          <span
                            key={property.id}
                            className="rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-foreground"
                          >
                            {property.name} · {property.type}
                          </span>
                        ),
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                      <span>
                        {sourceCopy.component.anatomy}{" "}
                        {selectedComponentSet?.anatomyCandidates.length ?? 0}
                        {selectedComponentSet?.anatomyCandidates.some(
                          (candidate) => (candidate.maxDepth ?? 1) > 1,
                        )
                          ? ` · ${sourceCopy.component.descendantEvidence}`
                          : ""}
                        {selectedComponentSet?.anatomyCoverage
                          ? ` · ${sourceCopy.component.bounded} ${selectedComponentSet.anatomyCoverage.visitedNodeCount}/${selectedComponentSet.anatomyCoverage.traversalNodeLimit}${selectedComponentSet.anatomyCoverage.traversalTruncated || selectedComponentSet.anatomyCoverage.candidateOutputTruncated ? ` · ${sourceCopy.component.truncated}` : ""}`
                          : ""}
                      </span>
                      <span>
                        {sourceCopy.component.unresolvedSlots}{" "}
                        {selectedComponentSet?.slotCandidates?.length ?? 0}
                      </span>
                      <span>
                        {sourceCopy.component.setIdentity}{" "}
                        {selectedComponentSet?.sourceNodeId ?? sourceCopy.component.unavailable}
                      </span>
                      <span>
                        {sourceCopy.component.variantIdentity}{" "}
                        {selectedComponentVariant?.sourceNodeId ??
                          sourceCopy.component.unavailable}
                      </span>
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex min-h-5 items-center gap-2 text-[11px] font-medium text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={componentOpacityTouched}
                        disabled={
                          componentRevisionLocked || !componentEditableSnapshot
                        }
                        onChange={(event) =>
                          setComponentOpacityTouched(
                            event.currentTarget.checked,
                          )
                        }
                      />
                      {sourceCopy.component.overrideOpacity}
                    </label>
                    <input
                      aria-label={sourceCopy.component.opacity}
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={componentOpacity}
                      disabled={
                        !componentOpacityTouched ||
                        componentRevisionLocked ||
                        !componentEditableSnapshot
                      }
                      onChange={(event) =>
                        setComponentOpacity(Number(event.currentTarget.value))
                      }
                      className="mt-1 h-5 w-full accent-foreground disabled:opacity-40"
                    />
                    <div className="text-right text-[10px] text-muted-foreground">
                      {componentOpacity.toFixed(2)}
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="flex min-h-5 items-center gap-2 text-[11px] font-medium text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={componentFillTouched}
                        disabled={componentRevisionLocked}
                        onChange={(event) =>
                          setComponentFillTouched(event.currentTarget.checked)
                        }
                      />
                      {sourceCopy.component.overrideFill}
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        aria-label={sourceCopy.component.fillColor}
                        type="color"
                        value={componentFillColor}
                        disabled={
                          !componentFillTouched || componentRevisionLocked
                        }
                        onChange={(event) =>
                          setComponentFillColor(event.currentTarget.value)
                        }
                        className="h-7 w-14 cursor-pointer rounded border border-border bg-background disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <span
                        className="text-[10px] text-muted-foreground"
                        data-testid="openpencil-component-fill-hex"
                      >
                        {componentFillColor.toLowerCase()}
                      </span>
                    </div>
                    <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
                      {sourceCopy.component.fillBoundary}
                    </p>
                  </div>
                </div>
              </section>
            ) : null}
            {selectedSurface && onReviseSurface ? (
              <section
                className="border-b border-border py-4"
                data-testid="openpencil-source-frame-editor"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-xs font-semibold text-foreground">
                      {sourceCopy.frame.title}
                      <span className="ml-2 font-normal text-muted-foreground">
                        {selectedSurface.name}
                      </span>
                    </h3>
                    <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                      {sourceCopy.frame.retained}
                    </p>
                    <p className="mt-1 text-[11px] leading-4 text-muted-foreground">
                      {sourceCopy.frame.boundary}
                    </p>
                  </div>
                  <button
                    type="button"
                    disabled={
                      !surfaceRevisionChanged ||
                      revisingSurface ||
                      revisionInFlight ||
                      !editName.trim() ||
                      editWidth <= 0 ||
                      editHeight <= 0
                    }
                    onClick={() => {
                      setRevisingSurface(true);
                      void onReviseSurface({
                        surfaceId: selectedSurface.id,
                        nodeId: selectedSurface.nodeId,
                        expectedName: selectedSurface.name,
                        ...(editName.trim() !== selectedSurface.name
                          ? { name: editName.trim() }
                          : {}),
                        ...(editWidth !== selectedSurface.width
                          ? { width: editWidth }
                          : {}),
                        ...(editHeight !== selectedSurface.height
                          ? { height: editHeight }
                          : {}),
                        ...(editVisibility !== "unchanged"
                          ? { visible: editVisibility === "visible" }
                          : {}),
                        ...(opacityTouched ? { opacity: editOpacity } : {}),
                        reason: `Update ${selectedSurface.name} from the Studio source editor.`,
                      })
                        .catch(() => undefined)
                        .finally(() => setRevisingSurface(false));
                    }}
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-foreground px-2.5 text-xs font-medium text-background hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Save className="h-3.5 w-3.5" aria-hidden />
                    {revisingSurface || revisionInFlight
                      ? sourceCopy.frame.revising
                      : sourceCopy.frame.create}
                  </button>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="text-[11px] font-medium text-muted-foreground sm:col-span-2">
                    {sourceCopy.frame.name}
                    <input
                      value={editName}
                      onChange={(event) =>
                        setEditName(event.currentTarget.value)
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    />
                  </label>
                  <label className="text-[11px] font-medium text-muted-foreground">
                    {sourceCopy.frame.width}
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={editWidth}
                      onChange={(event) =>
                        setEditWidth(Number(event.currentTarget.value))
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    />
                  </label>
                  <label className="text-[11px] font-medium text-muted-foreground">
                    {sourceCopy.frame.height}
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={editHeight}
                      onChange={(event) =>
                        setEditHeight(Number(event.currentTarget.value))
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    />
                  </label>
                  <label className="text-[11px] font-medium text-muted-foreground">
                    {sourceCopy.frame.visibility}
                    <select
                      value={editVisibility}
                      onChange={(event) =>
                        setEditVisibility(
                          event.currentTarget.value as typeof editVisibility,
                        )
                      }
                      className="mt-1 h-8 w-full rounded-md border border-border bg-background px-2 text-xs text-foreground outline-none focus:border-foreground"
                    >
                      <option value="unchanged">{sourceCopy.frame.keep}</option>
                      <option value="visible">{sourceCopy.frame.visible}</option>
                      <option value="hidden">{sourceCopy.frame.hidden}</option>
                    </select>
                  </label>
                  <div>
                    <label className="flex min-h-5 items-center gap-2 text-[11px] font-medium text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={opacityTouched}
                        onChange={(event) =>
                          setOpacityTouched(event.currentTarget.checked)
                        }
                      />
                      {sourceCopy.frame.overrideOpacity}
                    </label>
                    <input
                      aria-label={sourceCopy.frame.opacity}
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={editOpacity}
                      disabled={!opacityTouched}
                      onChange={(event) =>
                        setEditOpacity(Number(event.currentTarget.value))
                      }
                      className="mt-1 h-5 w-full accent-foreground disabled:opacity-40"
                    />
                    <div className="text-right text-[10px] text-muted-foreground">
                      {Math.round(editOpacity * 100)}%
                    </div>
                  </div>
                </div>
              </section>
            ) : null}
            <div className="max-h-[508px] overflow-auto">
              {selectedSurfaces.map((surface) => {
                const decision = workspace?.surfaceDecisions[surface.id];
                return (
                  <div
                    key={surface.id}
                    data-testid={`openpencil-surface-row-${surface.id}`}
                    data-selected={
                      surface.id === selectedSurfaceId ? "true" : "false"
                    }
                    className={`flex min-h-12 items-center gap-3 border-b border-border py-2 last:border-b-0 ${
                      surface.id === selectedSurfaceId
                        ? "border-l-2 border-l-primary pl-2"
                        : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <button
                        type="button"
                        aria-pressed={surface.id === selectedSurfaceId}
                        onClick={() => setSelectedSurfaceId(surface.id)}
                        className={`block w-full truncate text-left text-sm font-medium ${
                          surface.id === selectedSurfaceId
                            ? "text-primary"
                            : "text-foreground"
                        }`}
                      >
                        {surface.name}
                      </button>
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        {surface.width} × {surface.height} ·{" "}
                        {sourceCopy.surfaceClassification(surface.classification)}{" "}
                        {sourceCopy.frame.candidate}
                      </div>
                    </div>
                    <select
                      aria-label={`${surface.name} ${sourceCopy.frame.role}`}
                      value={
                        decision?.role ??
                        (surface.classification === "mobile"
                          ? "screen"
                          : "reference")
                      }
                      disabled={!onSurfaceDecision}
                      onChange={(event) =>
                        onSurfaceDecision?.(
                          surface.id,
                          decision?.status ?? "pending",
                          event.currentTarget.value as SourceSurfaceRole,
                        )
                      }
                      className="h-8 w-28 rounded-md border border-border bg-background px-2 text-xs text-foreground disabled:opacity-70"
                    >
                      {[
                        "screen",
                        "reference",
                        "annotation",
                        "cover",
                        "history",
                      ].map((role) => (
                        <option key={role} value={role}>
                          {sourceCopy.surfaceRole(role)}
                        </option>
                      ))}
                    </select>
                    <DecisionControl
                      label={surface.name}
                      status={decision?.status ?? "pending"}
                      onChange={
                        onSurfaceDecision
                          ? (status) =>
                              onSurfaceDecision(
                                surface.id,
                                status,
                                decision?.role ??
                                  (surface.classification === "mobile"
                                    ? "screen"
                                    : "reference"),
                              )
                          : undefined
                      }
                    />
                  </div>
                );
              })}
              {selectedSurfaces.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  {sourceCopy.frame.noCandidates}
                </div>
              ) : null}
            </div>
          </Panel>
        </div>
      ) : null}

      {!compact ? (
        <Panel
          title={sourceCopy.tokens.title}
          icon={Palette}
          testId="openpencil-token-candidate-panel"
          action={
            onBulkTokenDecision ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  title={sourceCopy.tokens.acceptAll}
                  aria-label={sourceCopy.tokens.acceptAll}
                  onClick={() => onBulkTokenDecision("accepted")}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Check className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  title={sourceCopy.tokens.rejectAll}
                  aria-label={sourceCopy.tokens.rejectAll}
                  onClick={() => onBulkTokenDecision("rejected")}
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : null
          }
        >
          <div className="grid max-h-[420px] gap-x-6 overflow-auto sm:grid-cols-2">
            {tokens.map((token) => {
              const decision = workspace?.tokenDecisions[token.id];
              const swatch =
                typeof token.value === "string" &&
                /^#[0-9a-f]{6,8}$/i.test(token.value)
                  ? token.value
                  : undefined;
              return (
                <div
                  key={token.id}
                  className="flex min-h-12 items-center gap-3 border-b border-border py-2"
                >
                  {swatch ? (
                    <span
                      className="h-5 w-5 shrink-0 rounded-sm border border-border"
                      style={{ backgroundColor: swatch }}
                    />
                  ) : null}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-foreground">
                      {token.name}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      {token.tokenType}
                      {token.value !== undefined
                        ? ` · ${String(token.value)}`
                        : ""}
                    </div>
                  </div>
                  <DecisionControl
                    label={token.name}
                    status={decision?.status ?? "pending"}
                    onChange={
                      onTokenDecision
                        ? (status) =>
                            onTokenDecision(
                              token.id,
                              status,
                              decision?.canonicalToken,
                            )
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className="mt-3 border-t border-border pt-3 text-xs text-muted-foreground">
            {sourceCopy.tokens.summary(
              progress.tokens.pending,
              progress.tokens.accepted,
              colorEvidence.length,
            )}
          </div>
          {onRegisterProjectDesignSystem ? (
            <div
              className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3"
              data-testid="openpencil-project-design-system-registration"
            >
              <button
                type="button"
                disabled={
                  registerProjectDesignSystemPending ||
                  progress.tokens.accepted === 0
                }
                onClick={onRegisterProjectDesignSystem}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              >
                {/* Same action as the Tokens screen's primary control, so it
                    carries the same words — it used to be called "Register as
                    this project's design system" here and "Apply these to this
                    project" there. */}
                {registerProjectDesignSystemPending
                  ? sourceCopy.tokens.applying
                  : registeredProjectDesignSystemId
                    ? sourceCopy.tokens.reapply
                    : sourceCopy.tokens.apply}
              </button>
              {registeredProjectDesignSystemId ? (
                <code
                  className="truncate text-[11px] text-muted-foreground"
                  title={registeredProjectDesignSystemId}
                >
                  {registeredProjectDesignSystemId}
                </code>
              ) : null}
              {projectDesignSystemNotice ? (
                <p className="basis-full text-xs text-muted-foreground">
                  {projectDesignSystemNotice}
                </p>
              ) : null}
            </div>
          ) : null}
        </Panel>
      ) : null}

      {showDeveloperDetails ? (
        <Panel
          title={sourceCopy.proof.title}
          icon={ShieldCheck}
          testId="openpencil-claim-boundary-panel"
        >
          <div className="grid gap-4 text-xs leading-5 text-muted-foreground md:grid-cols-3">
            <div>
              <div className="font-semibold text-foreground">
                {sourceCopy.proof.accepted}
              </div>
              <p>{bundle.claimBoundary.accepted[0]}</p>
            </div>
            <div>
              <div className="font-semibold text-foreground">
                {sourceCopy.proof.notClaimed}
              </div>
              <p>{bundle.claimBoundary.notClaimed[0]}</p>
            </div>
            <div>
              <div className="font-semibold text-foreground">
                {sourceCopy.proof.generation}
              </div>
              <p>{bundle.generationAdmission.requiredGateIds.join(", ")}</p>
            </div>
          </div>
        </Panel>
      ) : null}
          </div>
        </Panel>
      ) : null}
    </div>
  );
}
